import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSessionFromCloud, updateSessionParticipants, createInitialUserRecord } from '../utils/supabaseService';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

const JoinSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    // Load session from cloud database
    const loadSession = async () => {
      try {
        const result = await getSessionFromCloud(sessionId);
        
        if (result.success && result.data) {
          setSessionData(result.data);
          setLoading(false);
        } else {
          setError(result.error || 'Sesión no encontrada. Puede que haya expirado o el enlace sea incorrecto.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Error al cargar la sesión. Por favor, verifica tu conexión.');
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  const handleJoinSession = async () => {
    if (!userName.trim()) {
      alert('Por favor, introduce tu nombre');
      return;
    }

    try {
      console.log('👤 Guest: Joining session:', sessionId);
      console.log('👤 Guest: Current participants:', sessionData.participants);
      
      // Generate userId: nombreUsuario_HHmmss
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');
      const userId = `${userName.trim()}_${hours}${minutes}${seconds}`;
      console.log('👤 Guest: Generated userId:', userId);
      
      // Add new participant to the session
      const newParticipant = {
        id: userId,
        name: userName,
        status: 'connected',
        isHost: false,
        joinedAt: new Date().toISOString()
      };

      // Update participants in database
      const updatedParticipants = [
        ...sessionData.participants,
        newParticipant
      ];

      console.log('👤 Guest: Updating participants in database:', updatedParticipants);
      const result = await updateSessionParticipants(sessionId, updatedParticipants);
      console.log('👤 Guest: Update result:', result);

      // Crear registro inicial del invitado en user_claims
      console.log('👤 Guest: Creating initial user record in user_claims');
      const userRecordResult = await createInitialUserRecord(sessionId, userId, userName.trim());
      if (!userRecordResult.success) {
        console.error('❌ Guest: Error creating user record:', userRecordResult.error);
      } else {
        console.log('✅ Guest: User record created successfully');
      }

      // Navigate to bill overview with session data
      // sessionData already contains the bill_data object from Supabase
      navigate('/bill-overview', {
        state: {
          sessionId: sessionId,
          receiptData: sessionData,
          userName: userName,
          userId: userId,
          isGuest: true
        }
      });
    } catch (error) {
      console.error('👤 Guest: Error joining session:', error);
      alert('Error al unirse a la sesión. Por favor, intenta de nuevo.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-pulse-soft mb-4">
            <Icon name="Loader" size={48} className="text-primary mx-auto animate-spin" />
          </div>
          <p className="text-text-secondary">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 max-w-md">
          <div className="bg-surface border border-error rounded-lg p-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mb-4">
              <Icon name="AlertCircle" size={32} className="text-error" />
            </div>
            
            <h1 className="text-xl font-semibold text-foreground mb-2">
              Sesión no encontrada
            </h1>
            
            <p className="text-text-secondary text-sm mb-6">
              {error}
            </p>

            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                onClick={() => navigate('/receipt-scanner')}
              >
                <Icon name="Home" size={20} className="mr-2" />
                Volver al inicio
              </Button>
              
              <p className="text-xs text-text-secondary">
                💡 Las sesiones se eliminan automáticamente después de 24 horas
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-16 max-w-md">
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Icon name="Users" size={32} className="text-primary" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Únete a la sesión
            </h1>
            
            <p className="text-text-secondary text-sm">
              Te han invitado a dividir la cuenta
            </p>
          </div>

          {sessionData && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Receipt" size={20} className="text-text-secondary mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {sessionData.restaurant?.name || sessionData.restaurant || 'Restaurante'}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {sessionData.items?.length || 0} conceptos • {sessionData.total?.toFixed(2)}€
                  </p>
                  {sessionData.date && (
                    <p className="text-xs text-text-secondary mt-1">
                      {new Date(sessionData.date).toLocaleString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="userName" className="block text-sm font-medium text-foreground mb-2">
                Tu nombre
              </label>
              <input
                id="userName"
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Ej: María García"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoFocus
              />
              <p className="text-xs text-text-secondary mt-1">
                Los demás participantes verán este nombre
              </p>
            </div>

            <Button
              variant="default"
              fullWidth
              onClick={handleJoinSession}
              disabled={!userName.trim()}
            >
              <Icon name="LogIn" size={20} className="mr-2" />
              Unirse a la sesión
            </Button>
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-start gap-2 text-xs text-text-secondary">
              <Icon name="Info" size={14} className="mt-0.5" />
              <p>
                Al unirte podrás ver los conceptos de la cuenta y dividir los gastos entre todos los participantes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinSession;
